import React, { useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { formatIDR, parseIDR, formatDateID } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Search, Plus, Pencil, Trash2, Loader2, BookText, History } from "lucide-react";
import { toast } from "sonner";
import { useActiveTA } from "@/lib/academic";

function emptyForm() {
  return { nama: "", kelas_id: "", harga: "", tahun_ajaran: "", is_active: true, keterangan: "" };
}

export default function KitabPage() {
  const { user } = useAuth();
  const canEdit = user.role === "admin" || user.role === "sekretaris";
  const { active: activeTA } = useActiveTA();

  const [items, setItems] = useState([]);
  const [kelasList, setKelasList] = useState([]);
  const [q, setQ] = useState("");
  const [kelasFilter, setKelasFilter] = useState("all");
  const [activeFilter, setActiveFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const [historyKitab, setHistoryKitab] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (kelasFilter !== "all") params.kelas_id = kelasFilter;
      if (activeFilter !== "all") params.is_active = activeFilter === "active";
      const { data } = await api.get("/kitab", { params });
      setItems(data.items);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [q, kelasFilter, activeFilter]);

  const loadKelas = useCallback(async () => {
    try {
      const { data } = await api.get("/kelas", { params: { limit: 200 } });
      setKelasList(data.items || []);
    } catch (_e) { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadKelas(); }, [loadKelas]);

  const kelasName = useMemo(() => {
    const m = {};
    kelasList.forEach(k => { m[k.id] = k.nama; });
    return m;
  }, [kelasList]);

  const openCreate = () => {
    setEditing(null);
    const f = emptyForm();
    if (activeTA) f.tahun_ajaran = activeTA.tahun_ajaran;
    setForm(f);
    setDialogOpen(true);
  };
  const openEdit = (it) => {
    setEditing(it);
    setForm({
      nama: it.nama || "", kelas_id: it.kelas_id || "",
      harga: String(it.harga ?? ""), tahun_ajaran: it.tahun_ajaran || "",
      is_active: it.is_active !== false, keterangan: it.keterangan || "",
    });
    setDialogOpen(true);
  };
  const openHistory = async (it) => {
    setHistoryKitab(it);
    setHistoryOpen(true);
    setHistoryItems([]);
    try {
      const { data } = await api.get(`/kitab/${it.id}/history`);
      setHistoryItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.kelas_id) { toast.error("Kelas wajib dipilih"); return; }
    const hargaNum = parseIDR(form.harga);
    if (hargaNum < 0) { toast.error("Harga tidak valid"); return; }
    setSaving(true);
    try {
      const payload = {
        nama: form.nama, kelas_id: form.kelas_id, harga: hargaNum,
        tahun_ajaran: form.tahun_ajaran, is_active: form.is_active,
        keterangan: form.keterangan || null,
      };
      if (editing) {
        await api.patch(`/kitab/${editing.id}`, payload);
        toast.success("Kitab diperbarui");
      } else {
        await api.post("/kitab", payload);
        toast.success("Kitab ditambahkan");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/kitab/${deleteTarget.id}`);
      toast.success("Kitab dihapus");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="kitab-page">
      <div className="flex flex-col gap-1">
        <div className="text-xs font-semibold text-emerald-700 uppercase tracking-widest">Pembayaran</div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">Master Kitab</h1>
        <p className="text-slate-500 text-sm">Kelola daftar kitab per kelas beserta harganya.</p>
      </div>

      <Card className="p-4 md:p-5 border border-slate-200 shadow-sm bg-white">
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <div className="relative flex-1">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input placeholder="Cari nama kitab..." value={q} onChange={(e) => setQ(e.target.value)}
                   className="pl-9 h-10" data-testid="kitab-search-input" />
          </div>
          <Select value={kelasFilter} onValueChange={setKelasFilter}>
            <SelectTrigger className="w-full md:w-52 h-10" data-testid="kitab-kelas-filter">
              <SelectValue placeholder="Kelas" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Kelas</SelectItem>
              {kelasList.map((k) => <SelectItem key={k.id} value={k.id}>{k.nama}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={activeFilter} onValueChange={setActiveFilter}>
            <SelectTrigger className="w-full md:w-44 h-10" data-testid="kitab-active-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Status</SelectItem>
              <SelectItem value="active">Aktif</SelectItem>
              <SelectItem value="inactive">Nonaktif</SelectItem>
            </SelectContent>
          </Select>
          {canEdit && (
            <Button onClick={openCreate} className="bg-emerald-600 hover:bg-emerald-700 text-white h-10 gap-2"
                    data-testid="add-kitab-button">
              <Plus className="h-4 w-4" /> Tambah Kitab
            </Button>
          )}
        </div>
      </Card>

      <Card className="border border-slate-200 shadow-sm bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Nama Kitab</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Kelas</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Harga</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Tahun Ajaran</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Status</TableHead>
                {canEdit && <TableHead className="text-right text-xs uppercase tracking-wider text-slate-500 font-semibold">Aksi</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-400">
                  <Loader2 className="h-5 w-5 mx-auto animate-spin" />
                </TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-400">
                  Belum ada kitab.
                </TableCell></TableRow>
              ) : items.map((it) => (
                <TableRow key={it.id} data-testid={`kitab-row-${it.id}`} className="hover:bg-emerald-50/40">
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center">
                        <BookText className="h-4 w-4" />
                      </div>
                      <div>
                        <div className="font-semibold text-slate-900">{it.nama}</div>
                        {it.keterangan && <div className="text-xs text-slate-500">{it.keterangan}</div>}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-700">{kelasName[it.kelas_id] || "-"}</TableCell>
                  <TableCell className="text-sm font-semibold text-emerald-700 tabular-nums">{formatIDR(it.harga)}</TableCell>
                  <TableCell className="text-sm text-slate-700">{it.tahun_ajaran}</TableCell>
                  <TableCell>
                    <span className={`px-2.5 py-0.5 inline-flex text-xs font-semibold rounded-full ${
                      it.is_active ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"
                    }`}>
                      {it.is_active ? "Aktif" : "Nonaktif"}
                    </span>
                  </TableCell>
                  {canEdit && (
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => openHistory(it)} data-testid={`history-kitab-${it.id}`} title="Riwayat harga">
                        <History className="h-4 w-4 text-slate-600" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openEdit(it)} data-testid={`edit-kitab-${it.id}`}>
                        <Pencil className="h-4 w-4 text-slate-600" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(it)} data-testid={`delete-kitab-${it.id}`}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Form */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Kitab" : "Tambah Kitab"}</DialogTitle>
            <DialogDescription>Isi informasi kitab dan kelas yang menggunakannya.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <Label>Nama Kitab *</Label>
              <Input required value={form.nama} onChange={(e) => setForm(f => ({ ...f, nama: e.target.value }))}
                     data-testid="kitab-form-nama" placeholder="Mis. Safinatun Najah" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Kelas *</Label>
                <Select value={form.kelas_id} onValueChange={(v) => setForm(f => ({ ...f, kelas_id: v }))}>
                  <SelectTrigger data-testid="kitab-form-kelas"><SelectValue placeholder="Pilih kelas" /></SelectTrigger>
                  <SelectContent>
                    {kelasList.map(k => <SelectItem key={k.id} value={k.id}>{k.nama}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tahun Ajaran *</Label>
                <Input required value={form.tahun_ajaran} onChange={(e) => setForm(f => ({ ...f, tahun_ajaran: e.target.value }))}
                       data-testid="kitab-form-tahun" placeholder="Mis. 2025/2026" />
              </div>
            </div>
            <div>
              <Label>Harga (Rp) *</Label>
              <Input required type="text" inputMode="numeric" value={form.harga ? formatIDR(parseIDR(form.harga)).replace("Rp ", "") : ""}
                     onChange={(e) => setForm(f => ({ ...f, harga: e.target.value }))}
                     data-testid="kitab-form-harga" placeholder="50.000" />
              <p className="text-xs text-slate-400 mt-1">Hanya angka. Pemisah ribuan otomatis.</p>
            </div>
            <div>
              <Label>Keterangan</Label>
              <Textarea rows={2} value={form.keterangan} onChange={(e) => setForm(f => ({ ...f, keterangan: e.target.value }))}
                        data-testid="kitab-form-keterangan" placeholder="Catatan tambahan (opsional)" />
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm(f => ({ ...f, is_active: v }))}
                      data-testid="kitab-form-active" />
              <Label>Aktif (tampil pada form pembayaran)</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Batal</Button>
              <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="kitab-form-submit">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* History */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Riwayat Harga: {historyKitab?.nama}</DialogTitle>
            <DialogDescription>Perubahan harga kitab dari waktu ke waktu.</DialogDescription>
          </DialogHeader>
          <div className="max-h-80 overflow-y-auto">
            {historyItems.length === 0 ? (
              <div className="py-10 text-center text-sm text-slate-400">Belum ada perubahan harga.</div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {historyItems.map((h) => (
                  <li key={h.id} className="py-3 flex items-center justify-between">
                    <div>
                      <div className="text-sm text-slate-700">
                        <span className="line-through text-slate-400">{formatIDR(h.harga_lama)}</span>
                        <span className="mx-2 text-emerald-600">→</span>
                        <span className="font-semibold text-emerald-700">{formatIDR(h.harga_baru)}</span>
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">oleh {h.changed_by_name || "—"}</div>
                    </div>
                    <div className="text-xs text-slate-500">{formatDateID(h.changed_at)}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus Kitab?</AlertDialogTitle>
            <AlertDialogDescription>
              Kitab <span className="font-semibold">{deleteTarget?.nama}</span> akan dihapus permanen.
              Jika sudah ada pembayaran terkait, sistem akan menolak — silakan nonaktifkan saja.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} data-testid="kitab-delete-confirm" className="bg-red-600 hover:bg-red-700">
              Hapus
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
